/**
 * DOS518 - Digital Operations System for Wildfire Management
 * Copyright (c) 2024-2025 Angelo Irtilaç — All rights reserved.
 *
 * BriefingPrePartenza.tsx
 * Modale di avvio intervento:
 *  - Inserimento dati localizzazione (voce o tastiera)
 *  - Geocoding OpenStreetMap per coordinate da nome località
 *  - Pre-compilazione automatica scheda COAU
 *  - Pre-cache tile satellite area operativa
 *  - Registrazione avvio nel Diario Operativo
 */

import React, { useState, useRef, useCallback } from 'react';
import * as L from 'leaflet';
import {
  fetchElettrodottiOverpass,
  type ElettrodottoFeature,
} from '../../utils/elettrodottiOverpass';
import {
  getCachedElettrodotti,
  setCachedElettrodotti,
} from '../../utils/elettrodottiCache';
import { useSpeechInput } from '../../hooks/useSpeechInput';
import { parseVoiceTime } from '../common/MicInput';

// Converte parole numeriche italiane → cifre nel testo vocale (es. "via Roma cinque" → "via Roma 5")
const WORDS_TO_DIGITS: [RegExp, string][] = (() => {
  const map: Record<string, number> = {
    zero: 0, uno: 1, due: 2, tre: 3, quattro: 4,
    cinque: 5, sei: 6, sette: 7, otto: 8, nove: 9,
    dieci: 10, undici: 11, dodici: 12, tredici: 13,
    quattordici: 14, quindici: 15, sedici: 16, diciassette: 17,
    diciotto: 18, diciannove: 19,
    venti: 20, ventuno: 21, ventidue: 22, ventitre: 23,
    ventiquattro: 24, venticinque: 25, ventisei: 26, ventisette: 27,
    ventotto: 28, ventinove: 29,
    trenta: 30, trentuno: 31, trentadue: 32, trentatre: 33,
    trentaquattro: 34, trentacinque: 35, trentasei: 36, trentasette: 37,
    trentotto: 38, trentanove: 39,
    quaranta: 40, quarantuno: 41, quarantadue: 42, quarantatre: 43,
    quarantaquattro: 44, quarantacinque: 45, quarantasei: 46, quarantasette: 47,
    quarantotto: 48, quarantanove: 49,
    cinquanta: 50, cinquantuno: 51, cinquantadue: 52, cinquantatre: 53,
    cinquantaquattro: 54, cinquantacinque: 55, cinquantasei: 56, cinquantasette: 57,
    cinquantotto: 58, cinquantanove: 59,
    sessanta: 60, settanta: 70, ottanta: 80, novanta: 90,
    cento: 100, centouno: 101, centodue: 102, centotre: 103,
    duecento: 200, trecento: 300, quattrocento: 400, cinquecento: 500,
    seicento: 600, settecento: 700, ottocento: 800, novecento: 900,
    mille: 1000,
  };
  // Ordina per lunghezza decrescente per evitare sostituzioni parziali
  return Object.entries(map)
    .sort(([a], [b]) => b.length - a.length)
    .map(([word, num]) => [new RegExp(`\\b${word}\\b`, 'gi'), String(num)]);
})();

function wordsToDigits(text: string): string {
  let result = text;
  for (const [re, digit] of WORDS_TO_DIGITS) {
    result = result.replace(re, digit);
  }
  return result;
}
import { useAppStore } from '../../store/useAppStore';
import { precacheTiles, clearTileCache, clearTileCacheForArea, getCacheInfo, TileCacheProgress } from '../../utils/tileCacheManager';
import ProvinciaInput from '../common/ProvinciaInput';
import SiglaICAOInput from '../common/SiglaICAOInput';
import { v4 as uuidv4 } from 'uuid';

interface BriefingPrePartenzaProps {
  onClose: () => void;
  onAvvia: (lat: number, lon: number) => void; // centra mappa SITAC
}

interface BriefingFormData {
  dos: string;
  comune: string;
  provincia: string;
  localita: string;
  latitudine: string;
  longitudine: string;
  oraAccertamento: string;
  noteBreve: string;
}

const emptyForm = (): BriefingFormData => ({
  dos: '',
  comune: '',
  provincia: '',
  localita: '',
  latitudine: '',
  longitudine: '',
  oraAccertamento: new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }),
  noteBreve: '',
});

// Componente campo testo con pulsante microfono
interface VoiceFieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  readOnly?: boolean;
  hint?: string;
  inputStyle?: React.CSSProperties;
}

const VoiceField: React.FC<VoiceFieldProps> = ({
  label, value, onChange, placeholder, type = 'text', readOnly = false, hint, inputStyle
}) => {
  const [parseErr, setParseErr] = React.useState<string | null>(null);
  const { isListening, isSupported, startListening, stopListening, error } =
    useSpeechInput({
      onResult: (text) => {
        if (type === 'time') {
          const t = parseVoiceTime(text);
          if (t) { setParseErr(null); onChange(t); }
          else setParseErr(`Non riconosciuto: "${text}"`);
        } else {
          onChange(wordsToDigits(text));
        }
      },
    });

  return (
    <div style={styles.fieldWrap}>
      {label && <label style={styles.label}>{label}</label>}
      <div style={styles.inputRow}>
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          readOnly={readOnly}
          style={{ ...styles.input, background: readOnly ? '#1a2030' : '#0f1623', ...inputStyle }}
        />
        {isSupported && !readOnly && (
          <button
            onClick={isListening ? stopListening : startListening}
            style={{
              ...styles.micBtn,
              background: isListening ? '#ef4444' : '#1e3a5f',
              boxShadow: isListening ? '0 0 12px #ef4444' : 'none',
            }}
            title={isListening ? 'Ferma dettatura' : 'Detta vocalmente'}
          >
            {isListening ? '⏹' : '🎙️'}
          </button>
        )}
      </div>
      {hint && <span style={styles.hint}>{hint}</span>}
      {error && <span style={{ ...styles.hint, color: '#f87171' }}>{error}</span>}
      {parseErr && !isListening && <span style={{ ...styles.hint, color: '#f87171' }}>⚠ {parseErr}</span>}
      {isListening && (
        <span style={{ ...styles.hint, color: '#34d399', animation: 'pulse 1s infinite' }}>
          🔴 In ascolto… parla ora
        </span>
      )}
    </div>
  );
};

// ─── Componente principale ────────────────────────────────────

const BriefingPrePartenza: React.FC<BriefingPrePartenzaProps> = ({ onClose, onAvvia }) => {
  const [form, setForm] = useState<BriefingFormData>(emptyForm);
  const [geocoding, setGeocoding] = useState(false);
  const [geocodeError, setGeocodeError] = useState<string | null>(null);
  const [elecFeatures, setElecFeatures] = useState<ElettrodottoFeature[] | null>(null);
  const [elecLoading, setElecLoading] = useState(false);
  const [elecError, setElecError] = useState<string | null>(null);
  const elecAbortRef = useRef<AbortController | null>(null);
  const [cacheProgress, setCacheProgress] = useState<TileCacheProgress | null>(null);
  const [caching, setCaching] = useState(false);
  const [cacheInfo, setCacheInfo] = useState<{ tiles: number; sizeMB: number; areas: string[] } | null>(null);
  const [raggio, setRaggio] = useState(5);
  const abortRef = useRef<AbortController | null>(null);

  // Carica info cache all'apertura del modale
  React.useEffect(() => {
    getCacheInfo().then(setCacheInfo);
  }, []);

  const { updateCOAU, cambioDOS, addVoceDiario } = useAppStore();

  const setField = useCallback((field: keyof BriefingFormData) =>
    (value: string) => setForm(f => ({ ...f, [field]: value })),
    []
  );

  // Geocoding OpenStreetMap Nominatim
  const cercaCoordinate = async () => {
    const query = [form.localita, form.comune, form.provincia].filter(Boolean).join(', ');
    if (!query.trim()) {
      setGeocodeError('Inserisci almeno comune o località prima di cercare');
      return;
    }
    setGeocoding(true);
    setGeocodeError(null);
    try {
      const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=1&countrycodes=it`;
      const resp = await fetch(url, { headers: { 'Accept-Language': 'it' } });
      const data = await resp.json();
      if (data.length > 0) {
        setForm(f => ({
          ...f,
          latitudine: parseFloat(data[0].lat).toFixed(6),
          longitudine: parseFloat(data[0].lon).toFixed(6),
        }));
      } else {
        setGeocodeError('Località non trovata — inserisci le coordinate manualmente');
      }
    } catch {
      setGeocodeError('Errore di rete durante la ricerca');
    } finally {
      setGeocoding(false);
    }
  };

  // Pre-cache tile satellite
  const avviaCaching = async () => {
    const lat = parseFloat(form.latitudine);
    const lon = parseFloat(form.longitudine);
    if (isNaN(lat) || isNaN(lon)) {
      setGeocodeError('Coordinate necessarie per la pre-cache. Cerca prima la località.');
      return;
    }
    setCaching(true);
    setCacheProgress(null);
    abortRef.current = new AbortController();
    // areaId = "comune_provincia" per identificare l'area
    const areaId = [form.comune, form.provincia].filter(Boolean).join('_') || `${lat.toFixed(3)}_${lon.toFixed(3)}`;
    try {
      await precacheTiles(lat, lon, raggio, 10, 17, setCacheProgress, abortRef.current.signal, areaId, 'satellite');
      const info = await getCacheInfo();
      setCacheInfo(info);
    } catch (e: any) {
      if (e?.name !== 'AbortError') console.error('Cache error:', e);
    } finally {
      setCaching(false);
    }
  };

  const annullaCaching = () => {
    abortRef.current?.abort();
    setCaching(false);
  };

  const svuotaCacheCorrente = async () => {
    const areaId = [form.comune, form.provincia].filter(Boolean).join('_') || '';
    if (areaId) {
      await clearTileCacheForArea(areaId);
    }
    const info = await getCacheInfo();
    setCacheInfo(info);
    setCacheProgress(null);
  };

  const svuotaTutto = async () => {
    await clearTileCache();
    setCacheInfo({ tiles: 0, sizeMB: 0, areas: [] });
    setCacheProgress(null);
  };

  // Verifica rischi elettrodotti nell'area operativa
  const verificaElettrodotti = async () => {
    const lat = parseFloat(form.latitudine);
    const lon = parseFloat(form.longitudine);
    if (isNaN(lat) || isNaN(lon)) return;

    setElecLoading(true);
    setElecError(null);
    setElecFeatures(null);
    elecAbortRef.current?.abort();
    elecAbortRef.current = new AbortController();

    const delta = 0.05; // ~5 km
    const bounds = L.latLngBounds([lat - delta, lon - delta], [lat + delta, lon + delta]);

    try {
      const cached = await getCachedElettrodotti(bounds);
      if (cached) {
        setElecFeatures(cached.features);
        setElecLoading(false);
        return;
      }

      if (!navigator.onLine) {
        setElecError('Offline — attiva il layer in SITAC per pre-caricare i dati');
        setElecLoading(false);
        return;
      }

      const data = await fetchElettrodottiOverpass(bounds, elecAbortRef.current.signal);
      await setCachedElettrodotti(bounds, data);
      setElecFeatures(data.features);
    } catch (e: any) {
      if (e?.name === 'AbortError') return;
      setElecError(e?.message || 'Errore fetch elettrodotti');
    } finally {
      setElecLoading(false);
    }
  };

  // Avvia intervento — pre-compila COAU e registra nel diario
  const avviaIntervento = () => {
    if (!form.comune && !form.localita) {
      alert('⚠️ Inserisci almeno il Comune o la Località prima di avviare l\'intervento.');
      return;
    }
    try {
      const lat = parseFloat(form.latitudine);
      const lon = parseFloat(form.longitudine);

      updateCOAU({
        id: uuidv4(),
        data: new Date().toISOString(),
        indirizzo: [form.localita, form.comune, form.provincia].filter(Boolean).join(' — '),
        comune: form.comune,
        provincia: form.provincia,
        localita: form.localita,
        latitudine: form.latitudine,
        longitudine: form.longitudine,
        oraAccertamento: form.oraAccertamento,
        noteOperative: form.noteBreve,
        ...(form.dos.trim() ? { nominativoDOS: form.dos.trim() } : {}),
      } as any);

      if (form.dos.trim()) {
        cambioDOS(form.dos.trim(), `Avvio intervento — ${form.comune || form.localita}`);
      }

      addVoceDiario(
        'sistema', '🚒',
        `Intervento avviato — ${[form.localita, form.comune, form.provincia].filter(Boolean).join(', ')}` +
        (form.latitudine ? ` | GPS: ${form.latitudine}, ${form.longitudine}` : ''),
        { comune: form.comune, provincia: form.provincia, dos: form.dos }
      );

      if (!isNaN(lat) && !isNaN(lon)) {
        onAvvia(lat, lon);
      }

      onClose();
    } catch (err) {
      onClose();
    }
  };

  const coordValide = !isNaN(parseFloat(form.latitudine)) && !isNaN(parseFloat(form.longitudine));

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>

        {/* Header */}
        <div style={styles.header}>
          <div>
            <div style={styles.headerTitle}>🚒 BRIEFING PRE-PARTENZA</div>
            <div style={styles.headerSub}>Inserisci i dati dell'intervento — i campi saranno trascritti automaticamente nella Scheda COAU</div>
          </div>
          <button onClick={onClose} style={styles.closeBtn}>✕</button>
        </div>

        <div style={styles.body}>

          {/* Sezione DOS */}
          <div style={{ ...styles.section, borderLeftColor: '#f97316', borderLeftWidth: 4 }}>
            <div style={{ ...styles.sectionTitle, color: '#f97316' }}>
              <span style={styles.stepNum}>1</span> 👤 SIGLA DOS
            </div>
            <SiglaICAOInput
              value={form.dos}
              onChange={setField('dos')}
              placeholder="es. TF 014"
              hint="La sigla verrà usata anche in Fraseologia ICAO e in COAU → Personale e Mezzi"
            />
          </div>

          {/* Sezione Localizzazione */}
          <div style={{ ...styles.section, borderLeftColor: '#ef4444', borderLeftWidth: 4 }}>
            <div style={{ ...styles.sectionTitle, color: '#fca5a5' }}>
              <span style={styles.stepNum}>2</span> 📍 LOCALIZZAZIONE INCENDIO
            </div>
            <div style={styles.twoCol}>
              <VoiceField label="Comune" value={form.comune} onChange={setField('comune')} placeholder="es. Palermo" />
              <div style={styles.fieldWrap}>
                <label style={styles.label}>Provincia</label>
                <ProvinciaInput value={form.provincia} onChange={setField('provincia')} />
              </div>
            </div>
            <VoiceField
              label="Località / Toponimo"
              value={form.localita}
              onChange={setField('localita')}
              placeholder="es. Contrada Finaita, Monte Pellegrino"
              hint="Più dettagliato è, più precisa sarà la ricerca coordinate"
            />

            {/* Cerca coordinate */}
            <button
              onClick={cercaCoordinate}
              disabled={geocoding}
              style={{ ...styles.btnSecondary, marginTop: 8 }}
            >
              {geocoding ? '🔍 Ricerca in corso…' : '🔍 Cerca coordinate da località'}
            </button>
            {geocodeError && <div style={styles.errorMsg}>{geocodeError}</div>}

            <div style={styles.twoCol}>
              <VoiceField
                label="Latitudine"
                value={form.latitudine}
                onChange={setField('latitudine')}
                placeholder="es. 38.116900"
                hint="Formato decimale"
              />
              <VoiceField
                label="Longitudine"
                value={form.longitudine}
                onChange={setField('longitudine')}
                placeholder="es. 13.361700"
                hint="Formato decimale"
              />
            </div>
          </div>

          {/* Sezione Orario e Note */}
          <div style={{ ...styles.section, borderLeftColor: '#38bdf8', borderLeftWidth: 4 }}>
            <div style={styles.sectionTitle}>
              <span style={styles.stepNum}>3</span> 🕐 ORARIO E NOTE
            </div>
            <div style={styles.twoCol}>
              <VoiceField
                label="Ora accertamento incendio"
                value={form.oraAccertamento}
                onChange={setField('oraAccertamento')}
                placeholder="HH:MM"
                type="time"
              />
            </div>
            <VoiceField
              label="Note operative (briefing)"
              value={form.noteBreve}
              onChange={setField('noteBreve')}
              placeholder="es. Incendio di macchia mediterranea, vento da SW 30 km/h, 2 squadre a terra"
            />
          </div>

          {/* Sezione Pre-cache tile */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>
              <span style={styles.stepNum}>4</span> 🛰️ PRE-CACHE MAPPA SATELLITE
            </div>
            <div style={styles.cacheInfo}>
              Scarica le immagini satellite dell'area operativa per uso offline in campo.
              I dati vengono salvati sul dispositivo (storage permanente, non RAM).
            </div>

            {/* Stato cache attuale */}
            <div style={styles.cacheStatusBox}>
              {cacheInfo && cacheInfo.tiles > 0 ? (
                <>
                  <span style={{ color: '#34d399' }}>✅ Cache presente</span>
                  <span style={styles.cacheStatBadge}>
                    {cacheInfo.tiles.toLocaleString()} tile
                  </span>
                  <span style={styles.cacheStatBadge}>
                    {cacheInfo.sizeMB} MB
                  </span>
                  {cacheInfo.areas.length > 0 && (
                    <span style={{ ...styles.cacheStatBadge, background: '#1e3a5f' }}>
                      Aree: {cacheInfo.areas.join(', ')}
                    </span>
                  )}
                </>
              ) : (
                <span style={{ color: '#64748b' }}>⚠️ Nessuna cache presente — la mappa richiede connessione</span>
              )}
            </div>

            <div style={styles.twoCol}>
              <div style={styles.fieldWrap}>
                <label style={styles.label}>Raggio area (km)</label>
                <input
                  type="range"
                  min={2} max={15} step={1}
                  value={raggio}
                  onChange={(e) => setRaggio(Number(e.target.value))}
                  style={{ width: '100%', marginTop: 6 }}
                />
                <span style={{ ...styles.hint, color: '#94a3b8' }}>
                  {raggio} km — stimato ~{Math.round(raggio * raggio * 12)} tile · ~{Math.round(raggio * raggio * 0.18 * 10) / 10} MB
                </span>
              </div>
            </div>

            {cacheProgress && (
              <div style={styles.progressWrap}>
                <div style={styles.progressBar}>
                  <div style={{ ...styles.progressFill, width: `${cacheProgress.percent}%` }} />
                </div>
                <span style={styles.progressText}>
                  {cacheProgress.done
                    ? `✅ Completato — ${cacheProgress.downloaded} tile · ${Math.round(cacheProgress.downloaded * 0.015 * 10) / 10} MB stimati${cacheProgress.failed > 0 ? ` · ${cacheProgress.failed} fallite` : ''}`
                    : `⬇️ ${cacheProgress.downloaded}/${cacheProgress.total} tile (${cacheProgress.percent}%)`
                  }
                </span>
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' as const }}>
              {!caching ? (
                <button
                  onClick={avviaCaching}
                  disabled={!coordValide}
                  style={{
                    ...styles.btnSecondary,
                    opacity: coordValide ? 1 : 0.4,
                    cursor: coordValide ? 'pointer' : 'not-allowed',
                  }}
                  title={!coordValide ? 'Inserisci le coordinate prima' : ''}
                >
                  ⬇️ Scarica area satellite
                </button>
              ) : (
                <button onClick={annullaCaching} style={{ ...styles.btnSecondary, background: '#7f1d1d' }}>
                  ⏹ Annulla download
                </button>
              )}
              <button
                onClick={svuotaCacheCorrente}
                disabled={!form.comune && !form.provincia}
                style={{
                  ...styles.btnSecondary,
                  background: '#1c2333',
                  opacity: (form.comune || form.provincia) ? 1 : 0.4,
                }}
                title="Elimina solo la cache di questa area"
              >
                🗑️ Svuota area corrente
              </button>
              <button
                onClick={svuotaTutto}
                style={{ ...styles.btnSecondary, background: '#3f0f0f', color: '#fca5a5' }}
                title="Elimina tutta la cache satellite"
              >
                ⚠️ Svuota tutto
              </button>
            </div>
          </div>

          {/* Sezione Rischi Elettrodotti */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>
              <span style={styles.stepNum}>5</span> ⚡ RISCHI ELETTRODOTTI
            </div>
            <div style={styles.cacheInfo}>
              Verifica la presenza di linee ad Alta Tensione (AT/AAT) nell'area operativa.
              Indispensabile per operazioni con mezzi aerei (Canadair, elicotteri).
            </div>

            <button
              onClick={verificaElettrodotti}
              disabled={!coordValide || elecLoading}
              style={{
                ...styles.btnSecondary,
                background: 'rgba(204,0,0,0.25)',
                borderColor: '#CC0000',
                color: '#fca5a5',
                opacity: coordValide ? 1 : 0.4,
                cursor: coordValide ? 'pointer' : 'not-allowed',
              }}
              title={!coordValide ? 'Inserisci le coordinate prima' : ''}
            >
              {elecLoading ? '⏳ Verifica in corso…' : '⚡ Verifica rischi elettrodotti'}
            </button>

            {elecError && (
              <div style={styles.errorMsg}>{elecError}</div>
            )}

            {elecFeatures !== null && (
              elecFeatures.length === 0 ? (
                <div style={{ ...styles.cacheStatusBox, borderColor: 'rgba(52,211,153,0.4)' }}>
                  <span style={{ color: '#34d399' }}>✅ Nessun elettrodotto rilevato nel raggio di 5 km</span>
                </div>
              ) : (
                <div style={{ ...styles.cacheStatusBox, borderColor: 'rgba(204,0,0,0.5)', background: '#1a0808' }}>
                  <div style={{ width: '100%' }}>
                    <div style={{ color: '#f87171', fontWeight: 700, marginBottom: 6 }}>
                      ⚡ {elecFeatures.length} linee rilevate nell'area
                    </div>
                    {(['AAT', 'AT', 'MT', 'BT', 'sconosciuta'] as const).map(classe => {
                      const count = elecFeatures.filter(f => f.properties.classe === classe).length;
                      if (!count) return null;
                      const maxBuf = Math.max(...elecFeatures.filter(f => f.properties.classe === classe).map(f => f.properties.bufferMetri));
                      const color = classe === 'AAT' ? '#f87171' : classe === 'AT' ? '#fb923c' : classe === 'MT' ? '#fbbf24' : '#94a3b8';
                      return (
                        <div key={classe} style={{ fontSize: 12, color, marginBottom: 2 }}>
                          <b>{classe}</b>: {count} linee — buffer {maxBuf} m
                          {(classe === 'AAT') && <span style={{ color: '#f87171' }}> — vietato sorvolo &lt;100 m</span>}
                          {(classe === 'AT')  && <span style={{ color: '#fb923c' }}> — cautela elicotteri/Canadair</span>}
                        </div>
                      );
                    })}
                    {elecFeatures.some(f => f.properties.classe === 'AAT' || f.properties.classe === 'AT') && (
                      <div style={{ marginTop: 8, padding: '6px 8px', background: '#2a0808', border: '1px solid #CC0000', borderRadius: 4, fontSize: 12, color: '#fca5a5' }}>
                        ⚠️ Verificare posizione linee con ENAC prima di impiegare mezzi aerei
                      </div>
                    )}
                  </div>
                </div>
              )
            )}
          </div>

        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <button onClick={onClose} style={styles.btnAnnulla}>Annulla</button>

          {coordValide && (
            <a
              href={`https://www.google.com/maps/dir/?api=1&destination=${form.latitudine},${form.longitudine}&travelmode=driving`}
              target="_blank"
              rel="noopener noreferrer"
              style={styles.btnNaviga}
              title={`Naviga verso ${[form.localita, form.comune].filter(Boolean).join(', ') || 'il sito'}`}
            >
              🧭 Naviga
            </a>
          )}

          <button
            onClick={avviaIntervento}
            style={styles.btnAvvia}
          >
            🚒 AVVIA INTERVENTO
          </button>
        </div>

      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.4; }
        }
      `}</style>
    </div>
  );
};

// ─── Stili ────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.82)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 9999,
    padding: 16,
  },
  modal: {
    background: '#0d1520',
    border: '1px solid #1e3a5f',
    borderRadius: 12,
    width: '100%',
    maxWidth: 920,
    maxHeight: '92vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 24px 64px rgba(0,0,0,0.7)',
    overflow: 'hidden',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    padding: '24px 32px 20px',
    borderBottom: '1px solid #1e3a5f',
    background: 'linear-gradient(135deg, #0d1520 0%, #0f2040 100%)',
    flexShrink: 0,
  },
  headerTitle: {
    fontSize: 24, fontWeight: 700, color: '#f97316',
    letterSpacing: 1,
  },
  headerSub: {
    fontSize: 16, color: '#64748b', marginTop: 6,
  },
  closeBtn: {
    background: 'none', border: 'none',
    color: '#64748b', fontSize: 20, cursor: 'pointer',
    padding: '0 4px',
  },
  body: {
    overflowY: 'auto', padding: '24px 32px',
    display: 'flex', flexDirection: 'column', gap: 24,
  },
  section: {
    background: '#111827',
    border: '1px solid #1e3a5f',
    borderRadius: 10,
    padding: '22px 22px',
    display: 'flex', flexDirection: 'column', gap: 16,
    borderLeft: '1px solid #1e3a5f',
  },
  sectionTitle: {
    fontSize: 17, fontWeight: 700,
    color: '#38bdf8', letterSpacing: 0.6,
    textTransform: 'uppercase' as const,
    display: 'flex', alignItems: 'center', gap: 10,
  },
  stepNum: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 28, height: 28, borderRadius: '50%',
    background: '#1e3a5f', color: '#38bdf8',
    fontSize: 15, fontWeight: 900, flexShrink: 0,
  } as React.CSSProperties,
  twoCol: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 12,
  },
  fieldWrap: {
    display: 'flex', flexDirection: 'column', gap: 4,
  },
  label: {
    fontSize: 15, color: '#94a3b8', fontWeight: 600,
  },
  inputRow: {
    display: 'flex', gap: 8,
  },
  input: {
    flex: 1,
    padding: '14px 20px',
    borderRadius: 8,
    border: '1px solid #1e3a5f',
    color: '#e2e8f0',
    fontSize: 18,
    outline: 'none',
    minHeight: 56,
  },
  micBtn: {
    padding: '10px 14px',
    borderRadius: 8,
    border: 'none',
    cursor: 'pointer',
    fontSize: 22,
    transition: 'all 0.2s',
    flexShrink: 0,
    minWidth: 56, minHeight: 56,
  },
  hint: {
    fontSize: 14, color: '#475569',
  },
  btnSecondary: {
    padding: '11px 20px',
    borderRadius: 6,
    border: '1px solid #1e3a5f',
    background: '#1e3a5f',
    color: '#e2e8f0',
    fontSize: 15,
    cursor: 'pointer',
    fontWeight: 600,
  },
  errorMsg: {
    fontSize: 14, color: '#f87171',
    padding: '8px 12px',
    background: '#1a0a0a',
    borderRadius: 4,
    border: '1px solid #7f1d1d',
  },
  cacheInfo: {
    fontSize: 14, color: '#64748b',
    lineHeight: 1.6,
  },
  cacheStatusBox: {
    display: 'flex', flexWrap: 'wrap' as const, alignItems: 'center',
    gap: 8, padding: '12px 14px',
    background: '#0a1628',
    borderRadius: 6,
    border: '1px solid #1e3a5f',
    fontSize: 15,
  },
  cacheStatBadge: {
    padding: '3px 10px',
    borderRadius: 4,
    background: '#0f2a1a',
    color: '#34d399',
    fontSize: 14,
    fontWeight: 600,
  },
  progressWrap: {
    display: 'flex', flexDirection: 'column', gap: 6,
  },
  progressBar: {
    height: 10, background: '#1e293b',
    borderRadius: 4, overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    background: 'linear-gradient(90deg, #0ea5e9, #34d399)',
    borderRadius: 4,
    transition: 'width 0.3s ease',
  },
  progressText: {
    fontSize: 14, color: '#94a3b8',
  },
  footer: {
    display: 'flex', justifyContent: 'flex-end', gap: 14,
    padding: '20px 32px',
    borderTop: '1px solid #1e3a5f',
    flexShrink: 0,
    background: '#0d1520',
  },
  btnAnnulla: {
    padding: '12px 24px',
    borderRadius: 6,
    border: '1px solid #1e3a5f',
    background: 'transparent',
    color: '#64748b',
    fontSize: 15,
    cursor: 'pointer',
  },
  btnAvvia: {
    padding: '14px 36px',
    borderRadius: 10,
    border: 'none',
    background: 'linear-gradient(135deg, #f97316, #ea580c)',
    color: '#fff',
    fontSize: 17,
    fontWeight: 900,
    cursor: 'pointer',
    letterSpacing: 1,
    boxShadow: '0 6px 20px rgba(249,115,22,0.5)',
    textTransform: 'uppercase' as const,
    minHeight: 54,
  },
  btnNaviga: {
    padding: '12px 24px',
    borderRadius: 6,
    border: '1px solid #0ea5e9',
    background: 'linear-gradient(135deg, #0369a1, #0284c7)',
    color: '#fff',
    fontSize: 15,
    fontWeight: 700,
    cursor: 'pointer',
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    boxShadow: '0 4px 12px rgba(14,165,233,0.3)',
  },
};

export default BriefingPrePartenza;
